/**
 * Gas sponsorship, as the migration card sees it.
 *
 * The card asks two questions and this answers them: "can this wallet pay for
 * the next transaction, and if not, will AsetQu cover it" and "the migration is
 * done, can the gas be paid back". Everything else — which stage, how much, to
 * whom, whether the wallet is eligible at all — is decided by the server and
 * re-derived from the chain before any BNB moves.
 *
 * Two deliberate shapes:
 *
 *   - the wallet is only asked for a signature at the moment money is involved.
 *     Nobody signs anything to look at the page, and a wallet with enough BNB
 *     signs nothing at all until its migration has already succeeded.
 *   - every failure path ends with the migration still working. Sponsorship
 *     unavailable, refused, out of budget or switched off all reduce to the same
 *     card state: pay your own gas, which is what the portal did before this
 *     existed.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { Address, EIP1193Provider, Hex } from "viem";

import { browserPublicClient } from "./chain.client";
import type { WalletModel } from "./gas-sponsor";
import {
  SPONSOR_MESSAGES,
  SponsorRefusal,
  coversItsOwnGas,
  fetchPlan,
  fetchSnapshotStanding,
  fetchSponsorStatus,
  forgetSession,
  openSession,
  requestReimbursement,
  requestSponsorship,
  type SnapshotStanding,
  type SponsorPlan,
  type SponsorRecord,
  type SponsorStatus,
} from "./gas-sponsor";

/** Whether the wallet can now pay, and why not when it cannot. */
export type EnsureGasResult = { ok: boolean; message: string | null };

export type SponsorPhase =
  | "idle"
  | "checking"
  | "signing"
  | "sending"
  | "waiting-for-funds"
  | "funded"
  | "reimbursing"
  | "reimbursed"
  | "unavailable";

/**
 * How long to wait for sponsored BNB to arrive before telling the user to look
 * again.
 *
 * BSC produces a block roughly every three quarters of a second, so ninety
 * seconds is many dozens of blocks. Giving up is not the same as failing: the
 * payment is on chain and the ledger knows about it, so the card says "check
 * your wallet" rather than offering to send more.
 */
const fundingTimeoutMs = 90_000;
const pollIntervalMs = 3_000;

export type GasSponsorship = {
  status: SponsorStatus | null;
  /**
   * Where this wallet stands against the fixed eligibility snapshot, or null
   * while it is being read.
   *
   * The card reads this before promising anything. A wallet outside the set can
   * migrate exactly as before and pays its own gas, and saying so up front is
   * better than saying it after a wallet prompt.
   */
  standing: SnapshotStanding | null;
  balanceWei: bigint | null;
  phase: SponsorPhase;
  message: string | null;
  record: SponsorRecord | null;
  /** True while the wallet is being asked for something. */
  busy: boolean;
  refreshBalance: () => Promise<bigint | null>;
  /**
   * Makes sure the wallet can pay for a transaction costing `gasEstimate` at
   * `gasPriceWei`, sponsoring the shortfall if the server agrees to.
   *
   * `ok: false` means "do not open the wallet yet", and there is exactly one
   * situation that produces it: a sponsorship has been sent and the BNB has not
   * arrived. Everything else — sponsorship off, refused, out of budget, a
   * balance the portal could not read — answers `ok: true` with a note, because
   * the migration contract must stay usable when sponsorship is not. Blocking
   * there would turn a missing perk into a broken portal.
   *
   * The reason travels back with the answer rather than only through hook
   * state: the caller reads it immediately after awaiting, and React state
   * written during that await is not visible until the next render.
   */
  ensureGas: (
    gasEstimate: bigint,
    gasPriceWei: bigint,
  ) => Promise<EnsureGasResult>;
  /** Pays back the gas for a confirmed migration transaction. Never throws. */
  reimburse: (txHash: Hex) => Promise<void>;
  /**
   * The server's own view of this wallet, for the staging diagnostic panel.
   *
   * Costs a wallet signature the first time, and reuses the cached session
   * afterwards, so it is never called on the migration path — only when
   * somebody opens the panel and asks. Returns null rather than throwing: a
   * diagnostic that breaks the card would be worse than no diagnostic.
   */
  readPlan: () => Promise<SponsorPlan | null>;
  reset: () => void;
};

export function useGasSponsorship(
  address: Address | null,
  getProvider: () => EIP1193Provider | null,
): GasSponsorship {
  const [status, setStatus] = useState<SponsorStatus | null>(null);
  const [standing, setStanding] = useState<SnapshotStanding | null>(null);
  const [balanceWei, setBalanceWei] = useState<bigint | null>(null);
  const [phase, setPhase] = useState<SponsorPhase>("idle");
  const [message, setMessage] = useState<string | null>(null);
  const [record, setRecord] = useState<SponsorRecord | null>(null);

  // Guards the async paths below against a wallet change mid-flight: a
  // sponsorship requested for one address must not land in another's card.
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, [address]);

  useEffect(() => {
    let cancelled = false;
    void fetchSponsorStatus().then((value) => {
      if (!cancelled) setStatus(value);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const refreshBalance = useCallback(async () => {
    if (!address) return null;
    try {
      const value = await browserPublicClient.getBalance({ address });
      if (active.current) setBalanceWei(value);
      return value;
    } catch {
      // A balance the portal cannot read is not an error the user can act on.
      // The card falls back to offering the self-funded path, which is what it
      // would have done anyway.
      return null;
    }
  }, [address]);

  useEffect(() => {
    if (!address) return;

    let cancelled = false;
    void fetchSnapshotStanding(address).then((value) => {
      if (!cancelled) setStanding(value);
    });

    return () => {
      cancelled = true;
    };
  }, [address]);

  /* Read once per connected wallet, then only when something has moved it.
     Written through the promise callback rather than by calling refreshBalance
     here: the balance is external state this subscribes to, and a synchronous
     setState in an effect body is a cascading render. */
  useEffect(() => {
    if (!address) return;

    let cancelled = false;
    browserPublicClient
      .getBalance({ address })
      .then((value) => {
        if (!cancelled) setBalanceWei(value);
      })
      .catch(() => {
        // A balance the portal cannot read is not something the user can act
        // on. The card falls back to the self-funded path, which always works.
      });

    return () => {
      cancelled = true;
    };
  }, [address]);

  const readPlan = useCallback(async (): Promise<SponsorPlan | null> => {
    if (!address) return null;
    const provider = getProvider();
    if (!provider) return null;
    try {
      const token = await openSession(address, provider);
      return await fetchPlan(token);
    } catch {
      return null;
    }
  }, [address, getProvider]);

  const reset = useCallback(() => {
    setPhase("idle");
    setMessage(null);
    setRecord(null);
  }, []);

  const ensureGas = useCallback(
    async (
      gasEstimate: bigint,
      gasPriceWei: bigint,
    ): Promise<EnsureGasResult> => {
      if (!address) return { ok: false, message: null };

      const current = await refreshBalance();
      if (current === null) {
        // The portal could not read the balance. That is the portal's problem,
        // not the user's, and the wallet is about to tell them either way.
        setPhase("idle");
        return { ok: true, message: null };
      }
      if (coversItsOwnGas(current, gasEstimate, gasPriceWei, status?.walletModel)) {
        setPhase("idle");
        return { ok: true, message: null };
      }

      if (!status?.available) {
        setPhase("unavailable");
        setMessage("Sponsorship gas sedang tidak tersedia.");
        return { ok: true, message: null };
      }

      /* Outside the eligibility snapshot: nothing to ask for. Refused here
         rather than by the server so the wallet is never asked for a signature
         that cannot lead anywhere — and, as everywhere else in this file, the
         migration itself is left alone. */
      if (standing && !standing.eligibleForSponsoredGas) {
        setPhase("unavailable");
        setMessage(SPONSOR_MESSAGES.NOT_IN_SNAPSHOT);
        return { ok: true, message: null };
      }

      const provider = getProvider();
      if (!provider) {
        setPhase("unavailable");
        setMessage("Wallet tidak terdeteksi.");
        return { ok: true, message: null };
      }

      try {
        setMessage(null);
        setPhase("signing");
        const token = await openSession(address, provider);

        setPhase("sending");
        const sponsored = await requestSponsorship(token);
        if (!active.current) return { ok: false, message: null };
        setRecord(sponsored);

        setPhase("waiting-for-funds");
        const arrived = await waitForFunds(
          address,
          gasEstimate,
          gasPriceWei,
          status?.walletModel,
        );
        if (!active.current) return { ok: false, message: null };

        if (arrived === null) {
          const reason =
            "Gas sudah dikirim dan sedang dikonfirmasi jaringan. Periksa wallet Anda sebentar lagi.";
          setPhase("waiting-for-funds");
          setMessage(reason);
          return { ok: false, message: reason };
        }

        setBalanceWei(arrived);
        setPhase("funded");
        return { ok: true, message: null };
      } catch (error) {
        if (!active.current) return { ok: false, message: null };
        return handleRefusal(error, address, setPhase, setMessage, setRecord);
      }
    },
    [address, getProvider, refreshBalance, standing, status],
  );

  const reimburse = useCallback(
    async (txHash: Hex) => {
      if (!address || !status?.available) return;
      if (standing && !standing.eligibleForSponsoredGas) return;

      const provider = getProvider();
      if (!provider) return;

      try {
        setPhase("reimbursing");
        setMessage(null);
        const token = await openSession(address, provider);
        const settled = await requestReimbursement(token, txHash);
        if (!active.current) return;
        setRecord(settled);
        setPhase("reimbursed");
      } catch (error) {
        if (!active.current) return;
        handleRefusal(error, address, setPhase, setMessage, setRecord);
      }
    },
    [address, getProvider, standing, status],
  );

  return {
    status,
    standing,
    balanceWei,
    phase,
    message,
    record,
    busy:
      phase === "signing" ||
      phase === "sending" ||
      phase === "waiting-for-funds" ||
      phase === "reimbursing",
    refreshBalance,
    ensureGas,
    reimburse,
    readPlan,
    reset,
  };
}

/**
 * Polls until the wallet can pay, or gives up.
 *
 * Returns the balance once it is enough, or null on timeout. Null is not a
 * failure: the sponsor's transaction is on chain and recorded, and asking for
 * another one is exactly what the one-shot rule refuses. So the card waits, and
 * so does the user.
 */
async function waitForFunds(
  address: Address,
  gasEstimate: bigint,
  gasPriceWei: bigint,
  model: WalletModel | undefined,
): Promise<bigint | null> {
  const deadline = Date.now() + fundingTimeoutMs;

  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
    try {
      const balance = await browserPublicClient.getBalance({ address });
      if (coversItsOwnGas(balance, gasEstimate, gasPriceWei, model))
        return balance;
    } catch {
      // Keep polling: one unreachable endpoint is not the end of the wait.
    }
  }
  return null;
}

/**
 * Turns a refusal into card state.
 *
 * `ALREADY_SPONSORED` is the one worth singling out. It is not an error — it
 * means the BNB is already with the wallet — so the card shows the existing
 * record and points at the wallet rather than offering to send more.
 *
 * An expired session is cleared so the next attempt re-signs instead of
 * repeating a request that cannot succeed.
 */
function handleRefusal(
  error: unknown,
  address: Address,
  setPhase: (phase: SponsorPhase) => void,
  setMessage: (message: string | null) => void,
  setRecord: (record: SponsorRecord | null) => void,
): EnsureGasResult {
  if (error instanceof SponsorRefusal) {
    if (error.code === "ALREADY_SPONSORED" && error.record) {
      // The BNB is already with the wallet, or on its way to it. Asking again is
      // what the one-shot rule refuses, so the card waits rather than retries.
      setRecord(error.record);
      setPhase("funded");
      setMessage(error.message);
      return { ok: true, message: null };
    }
    if (error.code === "session_expired" || error.code === "no_session") {
      forgetSession(address);
    }
    setPhase("unavailable");
    setMessage(error.message);
    return { ok: true, message: null };
  }

  // A rejected signature is a decision, not a fault, and it is not a reason to
  // stop somebody migrating with their own BNB.
  setPhase("idle");
  setMessage(null);
  return { ok: true, message: null };
}
